/*!
 * WOOF Size Finder v1.0
 * Shopify size recommendation engine - no external dependencies
 */
(function (win, doc) {
  'use strict';

  /* ================================================================
     FALLBACK SIZE CHARTS - US Standard (ASTM D5585)
     Used when the page's size guide table cannot be parsed.
     All measurements in cm (body measurements, not garment).
  ================================================================ */
  var FALLBACK_WOMEN = {
    'XS':  { bust: [79,83],   waist: [60,64],   hip: [84,88]   },
    'S':   { bust: [84,88],   waist: [65,69],   hip: [89,93]   },
    'M':   { bust: [89,94],   waist: [70,75],   hip: [94,99]   },
    'L':   { bust: [95,101],  waist: [76,82],   hip: [100,106] },
    'XL':  { bust: [102,108], waist: [83,89],   hip: [107,113] },
    'XXL': { bust: [109,116], waist: [90,97],   hip: [114,121] },
    '3XL': { bust: [117,124], waist: [98,106],  hip: [122,130] }
  };
  var FALLBACK_MEN = {
    'XS':  { chest: [82,86],   waist: [67,71]   },
    'S':   { chest: [87,92],   waist: [72,77]   },
    'M':   { chest: [93,98],   waist: [78,83]   },
    'L':   { chest: [99,105],  waist: [84,90]   },
    'XL':  { chest: [106,113], waist: [91,98]   },
    'XXL': { chest: [114,121], waist: [99,106]  },
    '3XL': { chest: [122,130], waist: [107,115] }
  };

  /* ================================================================
     MEASUREMENT ALIASES - for table header normalisation
  ================================================================ */
  var MEASURE_ALIASES = {
    bust:     ['bust','brust','oberweite','brustweite','chest','poitrine'],
    waist:    ['waist','taille','taillenweite','tour de taille'],
    hip:      ['hip','hips','hüfte','hüftweite','hüftumfang'],
    chest:    ['chest','brust','brustumfang','brustweite'],
    inseam:   ['inseam','innenbein','innenbeinlänge'],
    shoulder: ['shoulder','schulter','schulterbreite'],
    length:   ['length','länge','körperlänge','body length']
  };

  /* Scoring weight per measurement - bust/chest matters most */
  var MEASURE_WEIGHTS = { bust:1.0, chest:1.0, waist:0.8, hip:0.7, inseam:0.9, shoulder:0.5, length:0.3 };

  /* Standard size labels - used to detect orientation of size tables */
  var SIZE_TOKENS = ['2XS','XXS','XS','S','M','L','XL','XXL','2XL','XXXL','3XL','4XL','5XL',
    '1X','2X','3X','4X',
    '30','32','34','36','38','40','42','44','46','48','50','52','54','56',
    '6','8','10','12','14','16','18','20','22','24','26'];

  /* ================================================================
     HELPERS
  ================================================================ */
  function normKey(str) {
    var lc = (str || '').toLowerCase().trim().replace(/\(.*?\)/g,'').trim();
    /* Standalone "width" (no body-part qualifier like "waist width") = flat chest/body width */
    if (/^width$/.test(lc)) return 'chest';
    for (var key in MEASURE_ALIASES) {
      var aliases = MEASURE_ALIASES[key];
      for (var i = 0; i < aliases.length; i++) {
        if (lc.indexOf(aliases[i]) !== -1) return key;
      }
    }
    return null;
  }

  function isSizeToken(str) {
    var s = (str || '').trim().toUpperCase().replace(/\s+/g,'').replace(/\(.*?\)/,'');
    return SIZE_TOKENS.indexOf(s) !== -1;
  }

  function parseRange(str) {
    if (!str) return null;
    var s = str.replace(/[^\d.,\-–]/g,'').replace(',','.');
    var m = s.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
    if (m) return [parseFloat(m[1]), parseFloat(m[2])];
    m = s.match(/^(\d+(?:\.\d+)?)$/);
    if (m) { var v = parseFloat(m[1]); return [v - 1.5, v + 1.5]; }
    return null;
  }

  function cmToFtIn(cm) {
    var totalIn = cm / 2.54;
    var ft = Math.floor(totalIn / 12);
    var inches = Math.round(totalIn % 12);
    if (inches === 12) { ft += 1; inches = 0; }
    return { ft: ft, inches: inches };
  }

  function gaussian(d, sigma) {
    return Math.exp(-(d * d) / (2 * sigma * sigma));
  }

  /* ================================================================
     SIZE GUIDE PARSER
  ================================================================ */
  function parseSizeGuideTable(table) {
    if (!table) return null;
    var rows = Array.from(table.querySelectorAll('tr'));
    if (rows.length < 2) return null;

    var grid = rows.map(function (r) {
      return Array.from(r.querySelectorAll('th,td')).map(function (c) {
        return c.textContent.trim();
      });
    });

    if (!grid[0] || grid[0].length < 2) return null;

    var firstRowHasSizes = grid[0].slice(1).some(isSizeToken);
    var firstColHasSizes = grid.slice(1).some(function (r) { return isSizeToken(r[0]); });

    var result = {};

    var tableIsImperial = false; /* will be set once unit is determined */
    var hasFlatMeasure  = false; /* true when any "width"/"across" header detected */
    var bodyMids        = [];    /* midpoints of non-flat measurements for fallback unit detection */

    if (firstRowHasSizes) {
      /* Format A: sizes across the top row */
      var headerSizes = grid[0].slice(1);
      headerSizes.forEach(function (sz, sIdx) {
        var key = sz.trim().toUpperCase().replace(/\(.*?\)/,'').trim();
        if (!isSizeToken(sz)) return;
        result[key] = {};
        grid.slice(1).forEach(function (row) {
          var mk = normKey(row[0]);
          if (!mk) return;
          var rng = parseRange(row[sIdx + 1]);
          if (rng) {
            var isFlat = /width|across/i.test(row[0]);
            if (isFlat) {
              hasFlatMeasure = true;
              var mid = (rng[0] + rng[1]) / 2;
              if (mid < 25) tableIsImperial = true;
              /* Elastic garments (waist/hip qualifier) → ×2.5 flat-to-body
                 Woven garments (chest/other, e.g. plain "Width") → ×1.7 flat-to-body */
              var isElastic = /waist|hip/i.test(row[0]);
              var baseMult  = isElastic ? 2.5 : 1.7;
              /* Use inch multiplier if ANY flat measurement in this table was in inches */
              var mult = tableIsImperial ? (2.54 * baseMult) : baseMult;
              rng = [Math.round(rng[0] * mult), Math.round(rng[1] * mult)];
            } else {
              bodyMids.push((rng[0] + rng[1]) / 2);
            }
            result[key][mk] = rng;
          }
        });
      });
    } else if (firstColHasSizes) {
      /* Format B: sizes down the first column */
      var headers = grid[0].slice(1);
      grid.slice(1).forEach(function (row) {
        var key = row[0].trim().toUpperCase().replace(/\(.*?\)/,'').trim();
        if (!isSizeToken(row[0])) return;
        result[key] = {};
        headers.forEach(function (h, hIdx) {
          var mk = normKey(h);
          if (!mk) return;
          var rng = parseRange(row[hIdx + 1]);
          if (rng) {
            var isFlat = /width|across/i.test(h);
            if (isFlat) {
              hasFlatMeasure = true;
              var mid = (rng[0] + rng[1]) / 2;
              if (mid < 25) tableIsImperial = true;
              var isElastic = /waist|hip/i.test(h);
              var baseMult  = isElastic ? 2.5 : 1.7;
              var mult = tableIsImperial ? (2.54 * baseMult) : baseMult;
              rng = [Math.round(rng[0] * mult), Math.round(rng[1] * mult)];
            } else {
              bodyMids.push((rng[0] + rng[1]) / 2);
            }
            result[key][mk] = rng;
          }
        });
      });
    }

    /* Fallback unit detection for body-measurement tables (Bust/Waist/Chest/Hip headers –
       no "width"/"across" to trigger flat detection). Body measurements in inches cluster
       around 30–55, in cm around 75–135. Threshold 60 cleanly separates them. */
    if (!hasFlatMeasure && bodyMids.length > 0) {
      var avgMid = bodyMids.reduce(function (a, b) { return a + b; }, 0) / bodyMids.length;
      tableIsImperial = avgMid < 60;
      /* Convert inch body measurements to cm so scoring always works in cm */
      if (tableIsImperial) {
        Object.keys(result).forEach(function (size) {
          Object.keys(result[size]).forEach(function (mk) {
            var rng = result[size][mk];
            if (rng) result[size][mk] = [Math.round(rng[0] * 2.54), Math.round(rng[1] * 2.54)];
          });
        });
      }
    }

    var valid = Object.keys(result).filter(function (k) {
      return Object.keys(result[k]).length > 0;
    });
    if (valid.length < 2) return null;

    var filtered = {};
    valid.forEach(function (k) { filtered[k] = result[k]; });
    filtered._imperial = tableIsImperial; /* true = table was in inches, false = cm */

    return filtered;
  }

  function findSizeGuideTable() {
    var selectors = [
      '[data-size-guide] table',
      '.size-guide table',
      '.size-guide-content table',
      '.size-guide-popup table',
      '.popup-size-chart table',
      '#size-guide table',
      '[id*="size-guide"] table',
      '[id*="size_guide"] table',
      '[class*="size-guide"] table',
      '[class*="size_guide"] table',
      '[class*="size-chart"] table',
      '[data-tab="size-guide"] table',
      '.product-size-chart table',
      'details table'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var t = doc.querySelector(selectors[i]);
      if (t) return t;
    }
    return null;
  }

  /* Async fallback: fetch the size-guide page and parse its first table */
  function fetchSizeGuideTable(callback) {
    var links = Array.from(doc.querySelectorAll('a[href]'));
    var guideLink = null;
    var patterns = [/size.?guide/i, /sizeguide/i, /size.?chart/i, /groessentabelle/i, /groessen/i, /masstabelle/i];
    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href') || '';
      for (var p = 0; p < patterns.length; p++) {
        if (patterns[p].test(href) || patterns[p].test(links[i].textContent)) {
          guideLink = href.startsWith('http') ? href : win.location.origin + href;
          break;
        }
      }
      if (guideLink) break;
    }
    if (!guideLink) { callback(null); return; }

    fetch(guideLink, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var fetchedDoc = parser.parseFromString(html, 'text/html');
        var table = fetchedDoc.querySelector('table');
        callback(parseSizeGuideTable(table));
      })
      .catch(function () { callback(null); });
  }

  /* ================================================================
     MEASUREMENT ESTIMATOR
     Formula: bust ≈ 0.55 × √(height × weight) + 31 (cm)
     Calibrated against US body stats (ASTM / CDC reference):
       Women 170cm/68kg → bust ~90 → US M  (89–94 cm)
       Women 163cm/55kg → bust ~83 → US XS/S
       Men   175cm/75kg → chest ~96 → US M  (93–98 cm)
       Men   180cm/90kg → chest ~103 → US L (99–105 cm)
  ================================================================ */
  var BODY_OFFSETS = {
    slim:    { bust: -4, chest: -4, waist: -5, hip: -3 },
    average: { bust:  0, chest:  0, waist:  0, hip:  0 },
    athletic:{ bust: +3, chest: +5, waist: -3, hip: +1 },
    full:    { bust: +3, chest: +3, waist: +7, hip: +9 }
  };

  function estimateMeasurements(heightCm, weightKg, gender, bodyType) {
    var hw  = Math.sqrt(heightCm * weightKg);
    var off = BODY_OFFSETS[bodyType] || BODY_OFFSETS.average;

    if (gender === 'men') {
      var chest = 0.60 * hw + 27 + off.chest;
      return {
        chest: chest,
        bust:  chest,
        waist: chest * 0.86  + off.waist,
        hip:   chest * 0.97  + off.hip
      };
    } else {
      var bust = 0.55 * hw + 31 + off.bust;
      return {
        bust:  bust,
        chest: bust,
        waist: bust * 0.82  + off.waist,
        hip:   bust * 1.075 + off.hip
      };
    }
  }

  /* ================================================================
     PROBABILITY ENGINE
  ================================================================ */
  var FIT_OFFSETS = {
    fitted:    { bust: -4, chest: -4, waist: -2, hip: -2 },
    regular:   { bust:  0, chest:  0, waist:  0, hip:  0 },
    oversized: { bust: +7, chest: +7, waist: +4, hip: +3 }
  };

  function calcProbabilities(measurements, sizeChart, fitPref) {
    var fitOff = FIT_OFFSETS[fitPref] || FIT_OFFSETS.regular;
    var sigma  = 2.5; /* cm - tighter bell curve → more confident top recommendation */
    var scores = {};

    Object.keys(sizeChart).forEach(function (size) {
      if (size.charAt(0) === '_') return; /* skip metadata keys like _imperial */
      var sizeMeasures = sizeChart[size];
      var totalW = 0, totalS = 0;

      Object.keys(sizeMeasures).forEach(function (mk) {
        var range = sizeMeasures[mk];
        if (measurements[mk] === undefined) return;
        var w    = MEASURE_WEIGHTS[mk] || 0.5;
        var eff  = measurements[mk] + (fitOff[mk] || 0);
        var mid  = (range[0] + range[1]) / 2;
        var dist;
        if (eff >= range[0] && eff <= range[1]) {
          dist = Math.abs(eff - mid) * 0.3; /* inside range: near-zero distance */
        } else {
          dist = Math.min(Math.abs(eff - range[0]), Math.abs(eff - range[1]));
        }
        totalS += w * gaussian(dist, sigma);
        totalW += w;
      });

      scores[size] = totalW > 0 ? totalS / totalW : 0;
    });

    /* Amplify differences: raise scores to power of 2 so top pick stands out clearly */
    Object.keys(scores).forEach(function (sz) { scores[sz] = Math.pow(scores[sz], 2); });

    var total = Object.values(scores).reduce(function (a, b) { return a + b; }, 0);
    if (total === 0) return [];

    var results = Object.keys(scores)
      .map(function (sz) {
        return { size: sz, probability: Math.round((scores[sz] / total) * 100) };
      })
      .filter(function (r) { return r.probability >= 4; })
      .sort(function (a, b) { return b.probability - a.probability; });

    /* Re-normalise after filtering small entries */
    var filteredTotal = results.reduce(function (a, r) { return a + r.probability; }, 0);
    return results.map(function (r) {
      return { size: r.size, probability: Math.round((r.probability / filteredTotal) * 100) };
    }).slice(0, 4);
  }

  /* ================================================================
     APPLY SIZE TO SHOPIFY VARIANT PICKER
     Tries multiple strategies to cover Dawn + common themes.
  ================================================================ */
  function applySize(size) {
    if (!size) return;

    /* Strategy 1: Dawn / modern themes - radio inputs inside variant-radios */
    var variantRadios = doc.querySelectorAll('variant-radios, variant-select-option');
    for (var i = 0; i < variantRadios.length; i++) {
      var radios = variantRadios[i].querySelectorAll('input[type="radio"]');
      for (var j = 0; j < radios.length; j++) {
        if (radios[j].value.toUpperCase() === size.toUpperCase()) {
          radios[j].click();
          var lbl = doc.querySelector('label[for="' + radios[j].id + '"]');
          if (lbl) lbl.click();
          return;
        }
      }
    }

    /* Strategy 2: Any radio with matching value near a size fieldset */
    var allRadios = doc.querySelectorAll(
      'fieldset input[type="radio"], .product-form__input input[type="radio"]'
    );
    for (var k = 0; k < allRadios.length; k++) {
      var r = allRadios[k];
      if (r.value.toUpperCase() === size.toUpperCase()) {
        r.checked = true;
        r.dispatchEvent(new Event('change', { bubbles: true }));
        var label = doc.querySelector('label[for="' + r.id + '"]');
        if (label) label.click();
        return;
      }
    }

    /* Strategy 3: Clickable labels / swatches */
    var labels = doc.querySelectorAll(
      '.variant-picker__option-values label, .product-form__input label, [data-option-values] label, .swatch-label'
    );
    for (var l = 0; l < labels.length; l++) {
      if (labels[l].textContent.trim().toUpperCase() === size.toUpperCase()) {
        labels[l].click();
        return;
      }
    }

    /* Strategy 4: Native select (legacy / Debut themes) */
    var selects = doc.querySelectorAll('select[name="id"], select[id*="option"]');
    for (var s = 0; s < selects.length; s++) {
      for (var o = 0; o < selects[s].options.length; o++) {
        var opt = selects[s].options[o];
        if (opt.text.trim().toUpperCase() === size.toUpperCase() ||
            opt.value.toUpperCase() === size.toUpperCase()) {
          selects[s].value = opt.value;
          selects[s].dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }

    /* Strategy 5: data-option-value buttons */
    var btns = doc.querySelectorAll('[data-option-value], .variant-button, .size-swatch');
    for (var b = 0; b < btns.length; b++) {
      var val = (btns[b].dataset.optionValue || btns[b].textContent || '').trim().toUpperCase();
      if (val === size.toUpperCase()) { btns[b].click(); return; }
    }
  }

  /* ================================================================
     MODAL CONTROLLER
  ================================================================ */
  var SizeFinder = {

    modals: [],

    init: function () {
      var self = this;
      doc.querySelectorAll('[data-size-finder]').forEach(function (modal) {
        self.setup(modal);
      });
    },

    setup: function (modal) {
      var self       = this;
      var showGender = modal.dataset.showGender === 'true';
      var defGender  = modal.dataset.defaultGender || 'women';

      /* Build ordered step list - skip gender step when not needed */
      var allSteps = Array.from(modal.querySelectorAll('[data-sf-step]'));
      var steps    = showGender
        ? allSteps
        : allSteps.filter(function (el) { return el.dataset.sfStep !== 'gender'; });

      var state = {
        modal:      modal,
        steps:      steps,
        totalSteps: steps.length,
        current:    0,
        showGender: showGender,
        defGender:  defGender,
        data: {
          gender:    showGender ? null : defGender,
          heightCm:  null,
          weightKg:  null,
          bodyType:  null,
          fit:       null
        },
        heightUnit:   'ftin',
        weightUnit:   'lbs',
        chartsLoaded: false,
        chartMetric:  null,  /* cm-based size chart */
        chartImperial: null  /* inch-based size chart */
      };

      this.modals.push(state);
      this.bindEvents(state);
    },

    /* ── Bind all DOM events for one modal ── */
    bindEvents: function (state) {
      var self  = this;
      var modal = state.modal;
      var id    = modal.id;

      /* Trigger button */
      var trigger = doc.querySelector('[data-size-finder-trigger][aria-controls="' + id + '"]');
      if (trigger) {
        trigger.addEventListener('click', function () { self.open(state); });
      }

      /* Close: backdrop + X button */
      modal.querySelectorAll('[data-sf-close]').forEach(function (el) {
        el.addEventListener('click', function () { self.close(state); });
      });

      /* Card selections */
      modal.querySelectorAll('[data-sf-select]').forEach(function (card) {
        card.addEventListener('click', function () {
          var field = card.dataset.sfSelect;
          var value = card.dataset.value;

          modal.querySelectorAll('[data-sf-select="' + field + '"]').forEach(function (c) {
            c.classList.remove('sf-card--selected');
          });
          card.classList.add('sf-card--selected');
          state.data[field] = value;


          /* Auto-advance */
          setTimeout(function () { self.goNext(state, true); }, 300);
        });
      });

      /* Next / Back */
      var nextBtn = modal.querySelector('[data-sf-next]');
      var backBtn = modal.querySelector('[data-sf-back]');
      if (nextBtn) nextBtn.addEventListener('click', function () { self.goNext(state, false); });
      if (backBtn) backBtn.addEventListener('click', function () { self.goBack(state); });

      /* Height unit toggle */
      modal.querySelectorAll('[data-sf-unit="height"]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          modal.querySelectorAll('[data-sf-unit="height"]').forEach(function (b) {
            b.classList.toggle('sf-unit-btn--active', b === btn);
          });
          self.switchUnit(state, 'height', btn.dataset.unit);
        });
      });

      /* Weight unit toggle */
      modal.querySelectorAll('[data-sf-unit="weight"]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          modal.querySelectorAll('[data-sf-unit="weight"]').forEach(function (b) {
            b.classList.toggle('sf-unit-btn--active', b === btn);
          });
          self.switchUnit(state, 'weight', btn.dataset.unit);
        });
      });

      /* Height inputs */
      var inCm = modal.querySelector('[data-sf-input="height_cm"]');
      var inFt = modal.querySelector('[data-sf-input="height_ft"]');
      var inIn = modal.querySelector('[data-sf-input="height_in"]');

      if (inCm) {
        inCm.addEventListener('input', function () {
          var v = parseFloat(inCm.value);
          if (!isNaN(v) && v >= 100 && v <= 250) {
            state.data.heightCm = v;
            if (state.heightUnit === 'ftin') {
              var fi = cmToFtIn(v);
              if (inFt) inFt.value = fi.ft;
              if (inIn) inIn.value = fi.inches;
            }
          }
        });
      }

      function syncFtIn() {
        var ft  = parseFloat((inFt && inFt.value) || 0);
        var ins = parseFloat((inIn && inIn.value) || 0);
        if (!isNaN(ft) && !isNaN(ins)) {
          var cm = Math.round(ft * 30.48 + ins * 2.54);
          if (cm >= 100 && cm <= 250) {
            state.data.heightCm = cm;
            if (inCm) inCm.value = cm;
          }
        }
      }
      if (inFt) inFt.addEventListener('input', syncFtIn);
      if (inIn) inIn.addEventListener('input', syncFtIn);

      /* Weight inputs */
      var inKg  = modal.querySelector('[data-sf-input="weight_kg"]');
      var inLbs = modal.querySelector('[data-sf-input="weight_lbs"]');

      if (inKg) {
        inKg.addEventListener('input', function () {
          var v = parseFloat(inKg.value);
          if (!isNaN(v) && v >= 20 && v <= 250) {
            state.data.weightKg = v;
            if (state.weightUnit === 'lbs' && inLbs) {
              inLbs.value = Math.round(v * 2.2046);
            }
          }
        });
      }
      if (inLbs) {
        inLbs.addEventListener('input', function () {
          var v = parseFloat(inLbs.value);
          if (!isNaN(v) && v >= 44 && v <= 550) {
            state.data.weightKg = v / 2.2046;
            if (inKg) inKg.value = Math.round(state.data.weightKg);
          }
        });
      }

      /* Fit slider */
      var fitSlider  = modal.querySelector('[data-sf-fit-range]');
      var fitLabelEl = modal.querySelector('[data-sf-fit-label]');
      var SF_FIT_VALUES = ['fitted', 'regular', 'oversized'];
      var SF_FIT_NAMES  = ['Fitted', 'Regular', 'Oversized'];

      if (fitSlider) {
        function updateFitSlider() {
          var idx = parseInt(fitSlider.value, 10);
          state.data.fit = SF_FIT_VALUES[idx];
          if (fitLabelEl) fitLabelEl.textContent = SF_FIT_NAMES[idx];
          var pct = (idx / 2) * 100;
          fitSlider.style.background =
            'linear-gradient(to right, #FF5300 ' + pct + '%, #e8e8e8 ' + pct + '%)';
        }
        fitSlider.addEventListener('input', updateFitSlider);
        updateFitSlider(); /* initialise to "Regular" */
      }

      /* Apply recommended size */
      modal.querySelector('[data-sf-apply-size]').addEventListener('click', function () {
        var size = this.dataset.size;
        if (size) { applySize(size); self.close(state); }
      });

      /* Restart */
      modal.querySelector('[data-sf-restart]').addEventListener('click', function () {
        self.restart(state);
      });

      /* ESC key */
      doc.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && !modal.hidden) self.close(state);
      });
    },

    /* ── Open ── */
    open: function (state) {
      var self = this;
      state.modal.hidden = false;
      doc.body.style.overflow = 'hidden';
      this.goToStep(state, 0);

      /* Lazy-load size charts (runs once per modal open) */
      if (!state.chartsLoaded) {
        state.chartsLoaded = true;

        /* Collect every parseable table on the page, separated by unit system */
        var allCandidates = [];
        var domTable = findSizeGuideTable();
        var domParsed = parseSizeGuideTable(domTable);
        if (domParsed) allCandidates.push(domParsed);

        Array.from(doc.querySelectorAll('table')).forEach(function (t) {
          /* Skip tables already found via targeted selector */
          if (t === domTable) return;
          var p = parseSizeGuideTable(t);
          if (p) allCandidates.push(p);
        });

        allCandidates.forEach(function (chart) {
          if (chart._imperial) {
            /* Prefer the imperial chart with the largest measurements (most complete) */
            if (!state.chartImperial) {
              state.chartImperial = chart;
            }
          } else {
            if (!state.chartMetric) {
              state.chartMetric = chart;
            }
          }
        });

        console.log('[SizeFinder] chartMetric:', JSON.stringify(state.chartMetric));
        console.log('[SizeFinder] chartImperial:', JSON.stringify(state.chartImperial));

        /* If no table found at all, try async fetch then fallback */
        if (!state.chartMetric && !state.chartImperial) {
          fetchSizeGuideTable(function (fetchedChart) {
            if (fetchedChart) {
              if (fetchedChart._imperial) state.chartImperial = fetchedChart;
              else state.chartMetric = fetchedChart;
            } else {
              var fb = state.defGender === 'men' ? FALLBACK_MEN : FALLBACK_WOMEN;
              state.chartMetric = fb;
            }
          });
          /* Use fallback immediately while fetch runs in background */
          state.chartMetric = state.defGender === 'men' ? FALLBACK_MEN : FALLBACK_WOMEN;
        }
      }
    },

    /* ── Close ── */
    close: function (state) {
      var modal = state.modal;
      modal.classList.add('sf-overlay--closing');
      setTimeout(function () {
        modal.hidden = true;
        modal.classList.remove('sf-overlay--closing');
        doc.body.style.overflow = '';
      }, 260);
    },

    /* ── Step Navigation ── */
    goToStep: function (state, index) {
      var modal = state.modal;
      state.steps.forEach(function (s) { s.classList.remove('sf-step--active'); });
      if (state.steps[index]) state.steps[index].classList.add('sf-step--active');
      state.current = index;

      /* Progress bar */
      var pct = state.totalSteps > 1
        ? Math.round((index / (state.totalSteps - 1)) * 100)
        : 0;
      var bar = modal.querySelector('[data-sf-progress]');
      if (bar) bar.style.width = pct + '%';

      /* Step label */
      var lbl = modal.querySelector('[data-sf-steps-label]');
      if (lbl) lbl.textContent = 'Step ' + (index + 1) + ' of ' + state.totalSteps;

      /* Back button */
      var backBtn = modal.querySelector('[data-sf-back]');
      if (backBtn) backBtn.hidden = (index === 0);

      /* Next button label */
      var nextBtn = modal.querySelector('[data-sf-next]');
      if (nextBtn) {
        var isLast = (index === state.totalSteps - 1);
        nextBtn.innerHTML = isLast
          ? 'See results <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M7 4l5 5-5 5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : 'Next <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M7 4l5 5-5 5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      }
    },

    goNext: function (state, fromCard) {
      if (!this.validate(state)) return;
      if (state.current < state.totalSteps - 1) {
        this.goToStep(state, state.current + 1);
      } else {
        this.showResult(state);
      }
    },

    goBack: function (state) {
      /* If result is visible, go back to last step */
      var result = state.modal.querySelector('[data-sf-result]');
      if (result && !result.hidden) {
        result.hidden = true;
        state.modal.querySelector('.sf-footer').hidden = false;
        this.goToStep(state, state.totalSteps - 1);

        var bar = state.modal.querySelector('[data-sf-progress]');
        if (bar) bar.style.width = '100%';
        return;
      }
      if (state.current > 0) this.goToStep(state, state.current - 1);
    },

    validate: function (state) {
      var step = state.steps[state.current];
      if (!step) return false;
      var type = step.dataset.sfStep;
      if (type === 'gender')       return state.data.gender   !== null;
      if (type === 'measurements') return state.data.heightCm !== null && state.data.weightKg !== null;
      if (type === 'bodytype')     return state.data.bodyType !== null;
      if (type === 'fit')          return state.data.fit      !== null;
      return true;
    },

    /* ── Unit switching ── */
    switchUnit: function (state, dimension, unit) {
      var modal = state.modal;

      if (dimension === 'height') {
        state.heightUnit = unit;
        modal.querySelectorAll('[data-unit-group="cm"]').forEach(function (el) {
          el.hidden = unit !== 'cm';
        });
        modal.querySelectorAll('[data-unit-group="ftin"]').forEach(function (el) {
          el.hidden = unit !== 'ftin';
        });
        if (state.data.heightCm && unit === 'ftin') {
          var fi = cmToFtIn(state.data.heightCm);
          var ft = modal.querySelector('[data-sf-input="height_ft"]');
          var ins = modal.querySelector('[data-sf-input="height_in"]');
          if (ft)  ft.value  = fi.ft;
          if (ins) ins.value = fi.inches;
        }
      } else if (dimension === 'weight') {
        state.weightUnit = unit;
        modal.querySelectorAll('[data-unit-group="kg"]').forEach(function (el) {
          el.hidden = unit !== 'kg';
        });
        modal.querySelectorAll('[data-unit-group="lbs"]').forEach(function (el) {
          el.hidden = unit !== 'lbs';
        });
        if (state.data.weightKg && unit === 'lbs') {
          var lbs = modal.querySelector('[data-sf-input="weight_lbs"]');
          if (lbs) lbs.value = Math.round(state.data.weightKg * 2.2046);
        }
      }
    },

    /* ── Show Result ── */
    showResult: function (state) {
      var modal = state.modal;
      var d     = state.data;

      /* Pick chart matching the user's chosen unit system.
         Falls back to the other unit system if only one table was found. */
      var useImperial = (state.heightUnit === 'ftin');
      var chart;
      if (useImperial) {
        chart = state.chartImperial || state.chartMetric;
      } else {
        chart = state.chartMetric || state.chartImperial;
      }
      /* If still nothing, use gender-specific fallback */
      if (!chart) chart = d.gender === 'men' ? FALLBACK_MEN : FALLBACK_WOMEN;

      var measurements  = estimateMeasurements(d.heightCm, d.weightKg, d.gender, d.bodyType);
      var probabilities = calcProbabilities(measurements, chart, d.fit);

      /* Safety fallback: if the parsed chart has no scoreable measurements (e.g. a garment
         table whose columns aren't recognised), fall back to the gender-specific body chart */
      if (probabilities.length === 0) {
        var fb = d.gender === 'men' ? FALLBACK_MEN : FALLBACK_WOMEN;
        probabilities = calcProbabilities(measurements, fb, d.fit);
      }

      console.log('[SizeFinder] chart:', JSON.stringify(chart));
      console.log('[SizeFinder] probabilities:', probabilities);

      /* Swap step content for result */
      state.steps.forEach(function (s) { s.classList.remove('sf-step--active'); });
      modal.querySelector('.sf-footer').hidden = true;

      var result = modal.querySelector('[data-sf-result]');
      result.hidden = false;

      /* Progress to 100% */
      var bar = modal.querySelector('[data-sf-progress]');
      if (bar) bar.style.width = '100%';
      var lbl = modal.querySelector('[data-sf-steps-label]');
      if (lbl) lbl.textContent = 'Your recommendation';

      /* Hero badge + confidence */
      var heroBadge = modal.querySelector('[data-sf-result-badge]');
      if (heroBadge && probabilities[0]) heroBadge.textContent = probabilities[0].size;
      var confEl = modal.querySelector('[data-sf-confidence-pct]');
      if (confEl && probabilities[0]) {
        confEl.textContent = probabilities[0].probability + '% confident this is your size';
      }

      /* Render size rows */
      var container = modal.querySelector('[data-sf-result-sizes]');
      container.innerHTML = '';

      probabilities.forEach(function (item, idx) {
        var best = idx === 0;
        var row  = doc.createElement('div');
        row.className = 'sf-size-row' + (best ? ' sf-size-row--best' : '');
        row.innerHTML =
          '<span class="sf-size-name">' + item.size + '</span>' +
          '<div class="sf-size-bar-wrap"><div class="sf-size-bar" data-w="' + item.probability + '"></div></div>' +
          '<span class="sf-size-pct">' + item.probability + '%</span>' +
          (best ? '<span class="sf-size-badge">Top</span>' : '');
        container.appendChild(row);
      });

      /* Animate bars on next paint */
      requestAnimationFrame(function () {
        setTimeout(function () {
          modal.querySelectorAll('[data-w]').forEach(function (bar) {
            bar.style.width = bar.dataset.w + '%';
          });
        }, 60);
      });

      /* Set apply-size button */
      var applyBtn = modal.querySelector('[data-sf-apply-size]');
      if (applyBtn && probabilities[0]) {
        applyBtn.textContent = 'Select size ' + probabilities[0].size;
        applyBtn.dataset.size = probabilities[0].size;
      }
    },

    /* ── Restart ── */
    restart: function (state) {
      var modal = state.modal;
      state.data = {
        gender:   state.showGender ? null : state.defGender,
        heightCm: null,
        weightKg: null,
        bodyType: null,
        fit:      'regular'
      };
      modal.querySelectorAll('[data-sf-select]').forEach(function (c) {
        c.classList.remove('sf-card--selected');
      });
      modal.querySelectorAll('[data-sf-input]').forEach(function (i) { i.value = ''; });

      /* Reset fit slider to middle (Regular) */
      var fitSlider = modal.querySelector('[data-sf-fit-range]');
      if (fitSlider) {
        fitSlider.value = 1;
        fitSlider.dispatchEvent(new Event('input'));
      }

      /* Reset body type image selection highlight (images stay, just remove selected state) */
      modal.querySelectorAll('[data-sf-select="bodyType"]').forEach(function (opt) {
        opt.classList.remove('sf-card--selected');
      });

      var result = modal.querySelector('[data-sf-result]');
      if (result) result.hidden = true;
      modal.querySelector('.sf-footer').hidden = false;

      this.goToStep(state, 0);
    }
  };

  /* ── Boot ── */
  function boot() { SizeFinder.init(); }
  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  win.WOOF_SizeFinder = SizeFinder;

}(window, document));
